import React, { Fragment, useMemo } from "react";
import { Box, Button, Fade, Grid2 as Grid, IconButton, Stack, Typography } from "@mui/material";
import { GenericIconButton } from "../../../components/generic/GenericIconButton";
import { Heart, Link, UserCircle } from "@phosphor-icons/react";
import { COUNTRIES_WITH_ISO } from "../../../enums/countries";
import { filterArray } from "../../../helpers/arrayHelper";
import { PLATFORMS_KEY, SOCIALS_KEY } from "../../../enums/generalEnums";
import { WHITE_OUTLINED_ICONS, WHITE_PLATFORM_ICONS } from "../../../enums/iconsEnums";
import { router, usePage } from "@inertiajs/react";
import { GenericFlag } from "../../../components/generic/GenericFlag";
import useStore from "../../../states/store";
import { useCreateUserPreference } from "../../../components/generic/functions/genericFunctions";
import { shortenNumber, toSlug, withHTTP } from "../../../helpers/stringHelper";

export const CreatorCard = ({ creator, width = '250px', height = '350px', lazyLoad = true }) => {
  const { auth } = usePage().props;
  const [{ userPreference }, { setSignUpDialogState }] = useStore();
  const isSelected = userPreference?.saved_profiles?.includes(creator?.id);
  const createUserPreference = useCreateUserPreference();

  const socialsAndPlatforms = useMemo(() => {
    return {
      platforms: filterArray(creator?.category_values, PLATFORMS_KEY, 'category.type') ?? [],
      socials: filterArray(creator?.category_values, SOCIALS_KEY, 'category.type') ?? [],
    };
  }, [creator]);

  return (
    <Fade in={true}>
      <Box sx={{ display: "grid", position: 'relative', width: width, height: height, borderRadius: '16px', overflow: 'hidden', gridTemplateAreas: `"overlap"` }}>
        <Box
          gridArea="overlap"
          zIndex={1}
          onClick={() => router.visit(`/creator?user=${toSlug(creator?.name)}-${creator?.id}`)}
          sx={{ cursor: "pointer" }}
        >
          <img
            src={creator?.details?.profile_picture?.location}
            alt="Creator Profile"
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transition: "opacity 0.3s ease-in-out",
            }}
            loading={lazyLoad ? "lazy" : "eager"}
          />
        </Box>

        <Box
          gridArea="overlap"
          position="relative"
          zIndex={1}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0) 60%, rgba(0, 0, 0, 1) 100%)',
            pointerEvents: "none"
          }}
        />

        <Box
          gridArea="overlap"
          position="relative"
          zIndex={3}
          sx={{
            zIndex: 1,
            padding: '16px',
            display: 'flex',
            color: '#FFFF',
            flexDirection: 'column',
            justifyContent: 'space-between',
            height: '100%',
            pointerEvents: "none",
          }}
        >
          <Stack justifyContent={'space-between'} direction={'row'}>
            {
              (creator?.id !== auth?.user?.id) ? <GenericIconButton
                color={isSelected ? 'error.main' : '#FFF'}
                onClick={() => auth?.user ? createUserPreference(isSelected, creator?.id) : setSignUpDialogState(true)}
                icon={<Heart size={24} weight={isSelected ? 'fill' : 'bold'} />}
              /> : <div></div>
            }
            <Box sx={{ display: { xs: 'none', sm: 'flex' }, pointerEvents: "auto" }}>
              <Button variant="contained" onClick={() => router.visit(`/creator?user=${toSlug(creator?.name)}-${creator?.id}`)}>View Profile</Button>
            </Box>
            <Box sx={{ display: { xs: 'flex', sm: 'none' }, pointerEvents: "auto" }}>
              <IconButton
                onClick={() => router.visit(`/creator?user=${toSlug(creator?.name)}-${creator?.id}`)}
                sx={{
                  backgroundColor: 'primary.main',
                  color: '#FFF',
                  height: '42px',
                  width: '42px',
                  ':hover': {
                    backgroundColor: 'primary.main',
                  }
                }}
              >
                <UserCircle size={16} weight="bold" />
              </IconButton>
            </Box>
          </Stack>
          <Box>
            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={1}>
              <Typography sx={{ cursor: 'pointer' }} fontSize={{ xs: '18px', sm: '20px' }} fontWeight={700} color="primary" noWrap textTransform={'capitalize'}>{creator?.name}</Typography>
              <GenericFlag code={COUNTRIES_WITH_ISO?.[creator?.details?.country]} />
            </Stack>
            <Typography fontSize={{ xs: '12px', sm: '14px' }} noWrap lineHeight={'16px'}>{creator?.details?.profession ?? 'None'}</Typography>
            <Grid container spacing={3} mt={1.2}>
              {
                socialsAndPlatforms?.platforms?.filter((item) => !!item?.value)?.slice(0, 6)?.map((platform, index) => {
                  const renderIcon = WHITE_PLATFORM_ICONS?.[platform?.category?.name] ?? null;
                  const platformValue = withHTTP(platform?.value) ? platform?.value : `https://${platform?.value}`;
                  return (
                    <Grid item key={`platform-key-${index}`} size={2} sx={{ cursor: 'pointer', pointerEvents: "auto" }} onClick={() => window.open(platformValue, '_blank')}>
                      {renderIcon ? <img src={renderIcon} style={{ height: '18px' }} /> : <Link size={20} />}
                    </Grid>
                  );
                })
              }
            </Grid>
            <Grid container mt={0.1} columnSpacing={{ xs: 2, xsm: 4.5 }} rowSpacing={0}>
              {
                socialsAndPlatforms?.socials?.filter((item) => (!!item?.value && item?.category?.meta?.showFollowerCount && item?.category?.meta?.featured))
                  ?.sort((a, b) => (b?.meta?.followerCount ?? 0) - (a?.meta?.followerCount ?? 0))
                  ?.map((social, index) => {
                    const renderIcon = <Box mt={{ xs: 0.1, sm: 0.4 }}>{WHITE_OUTLINED_ICONS?.[social?.category?.name]}</Box> ?? null;
                    const socialValue = withHTTP(social?.value) ? social?.value : `https://${social?.value}`;
                    return (
                      <Fragment key={`social-key-${index}`} >
                        {
                          social?.value && <Grid item sx={{ cursor: 'pointer' }} size={4} onClick={() => window.open(socialValue, '_blank')}>
                            <Stack direction={'row'} gap={{ xs: 0.5 }} sx={{ pointerEvents: "auto" }}>
                              {renderIcon ?? <Link size={20} />}
                              <Typography fontSize={{ xs: '12px', sm: '14px' }}>{shortenNumber(social?.meta?.followerCount ?? 0) ?? 0}</Typography>
                            </Stack>
                          </Grid>
                        }
                      </Fragment>
                    );
                  })
              }
            </Grid>
          </Box>
        </Box>
      </Box>
    </Fade >
  );
};